import { MenuDivider, MenuItem } from '@apollo/orbit';
import { LocationDescriptorObject } from 'history';
import React, { useMemo } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { useCloudOnboardingRootURL } from 'src/hooks/useCloudOnboardingRootURL';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { RoverInitMenuItem } from './RoverInitMenuItem';

/**
 * GraphCreationOptions
 *
 * Renders dropdown menu items for the graph creation button.
 * It prepares the primary button configuration and menu items based on user plan type,
 * feature flags, and available capabilities.
 *
 * Logic:
 * 1. Menu Items Construction:
 *    - Shows "Connect an existing graph" as the first menu item
 *    - When isCloudOnlyPlan is false:
 *      - Shows the Rover init terminal command UI
 *
 * 2. Destination Logic:
 *    - For Cloud-Only Plans (Dedicated or Serverless): Links to New Supergraph flow
 *      via createGraphLink from useCloudOnboardingRootURL
 *    - For other plans: Links to New Classic Graph modal via setOverlayVisibleLocation
 *
 * @returns {Object} An object containing:
 *    - menuItems: React elements for dropdown menu or undefined if no items should be shown
 */
export const GraphCreationOptions = ({
  accountId,
  isCloudOnlyPlan,
}: {
  accountId: string;
  isCloudOnlyPlan: boolean;
}): {
  menuItems: React.JSX.Element[];
  defaultAction: {
    to: string | LocationDescriptorObject;
    onClick: () => void;
  };
} => {
  const location = useLocation();
  const createGraphLink = useCloudOnboardingRootURL({ accountId });

  const defaultAction = {
    to: isCloudOnlyPlan
      ? createGraphLink
      : setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.createGraph,
        }),
    onClick: () => ampli.connectGraphFlowStarted({ 'API Type': 'GraphQL' }),
  };

  // Create MenuItem element(s)
  // Helper function for creating the GraphQL API connection menu item

  const menuItems = useMemo(() => {
    const items = [
      <MenuItem
        key="connect-existing-graphql"
        name="Connect an existing graph"
        as={ReactRouterLink}
        onClick={defaultAction.onClick}
        to={defaultAction.to}
      >
        Connect an existing graph
      </MenuItem>,
    ];

    if (!isCloudOnlyPlan) {
      items.push(<MenuDivider key="divider" />);
      items.push(<RoverInitMenuItem key="rover-init" />);
    }

    return items;
  }, [defaultAction.onClick, defaultAction.to, isCloudOnlyPlan]);

  return {
    menuItems,
    defaultAction,
  };
};
